import { toBigNumber } from '@1trading/react';
import BigNumber from 'bignumber.js';
import { bopApi, fastApi } from '@/lib/api/client';
import { isNotFoundError } from '@/lib/api/error';

const INSTRUMENTS_PATH = 'v1/instruments';
const UPDATE_MARKET_PATH = 'v1/markets/update';
const UPDATE_MARKETS_BULK_PATH = 'v1/markets/updateBulk';
const PERP_ORDER_LIMITS_PATH = 'v1/markets/perpOrderLimits';
const POSITION_LIMITS_PATH = 'v1/markets/positionLimits';
const UPDATE_MARKET_LIMITS_PATH = 'v1/markets/updateLimits';
const FREEZE_EXCHANGE_PATH = 'v1/exchange/freeze';
const UNFREEZE_EXCHANGE_PATH = 'v1/exchange/unfreeze';
const KILL_EXCHANGE_PATH = 'v1/exchange/kill';

// FAST vocabulary: instruments use EQUITY_FUTURE — never bop-api's
// Dynamo-schema EQUITY_PERP, which this UI never sees.
export const MARKET_TYPES = ['SPOT', 'PERP', 'EQUITY_FUTURE', 'DATED_FUTURE'] as const;
export type MarketType = (typeof MARKET_TYPES)[number];

// Deliberate fix: legacy also declared 'INACTIVE' — dead value, never
// produced by FAST and not accepted by bop-api. Dropped from the port.
export const MARKET_STATES = ['ACTIVE', 'POST_ONLY', 'SUSPENDED', 'CLOSED'] as const;
export type MarketState = (typeof MARKET_STATES)[number];

// The customer segments the exchange keeps a max position cap for, in display order.
export const POSITION_LIMIT_SEGMENTS = ['RETAIL', 'INSTITUTIONAL', 'MARKET_MAKER'] as const;
export type PositionLimitSegment = (typeof POSITION_LIMIT_SEGMENTS)[number];

export const POSITION_LIMIT_SEGMENT_LABELS: Record<PositionLimitSegment, string> = {
  RETAIL: 'Retail',
  INSTITUTIONAL: 'Institutional',
  MARKET_MAKER: 'Market Maker',
};

/** Max position size per customer segment, quote notional as decimal strings. */
export type MaxPositionSize = Record<PositionLimitSegment, string>;

export interface MarketAsset {
  code: string;
  precision: number;
}

/** Instrument from GET {FAST_API_BASE_URL}/v1/instruments (state flattened for the current environment). */
export interface Market {
  base: MarketAsset;
  quote: MarketAsset;
  amount_precision: number;
  market_precision: number;
  /** decimal string, e.g. "10.0" */
  min_size: string;
  min_price: string;
  max_price: string;
  /** e.g. "BTC_EUR", "BTC_USD_P" */
  id: string;
  type: MarketType;
  state: MarketState;
  /**
   * Futures only: the price collar the public API publishes — how far, as a percent, an order
   * price may sit from the mark price. Decimal string ("10"), or null when the exchange did not
   * list the market at seed time. Seeded from the exchange and mirrored on every order-limits
   * save, so it can drift like max_order_value.
   */
  price_collar_percentage?: string | null;
  /**
   * Futures only: the max single-order notional the public API publishes. Decimal string, or
   * null when the exchange enforces no cap. Mirrored from the exchange into Dynamo by the
   * seed and by every limits save, so it can drift from the exchange's live value — see
   * getOrderLimitDrift. Absent entirely until the environment has been re-seeded.
   */
  max_order_value?: string | null;
  /**
   * Futures only: the max position size the public API publishes per customer segment — what
   * the exchange enforces, i.e. the segment's own cap or the market default. Null when the
   * exchange reported none. Mirrored like max_order_value — see getPositionLimitDrift.
   */
  max_position_size?: MaxPositionSize | null;
}

export const FUTURES_MARKET_TYPES: readonly MarketType[] = ['PERP', 'EQUITY_FUTURE', 'DATED_FUTURE'];

/** Gates the limits pencil. */
export const isFuturesMarket = (market: Market): boolean => FUTURES_MARKET_TYPES.includes(market.type);

// Label map: the UI presents 'POST ONLY' (space); the wire value is 'POST_ONLY'.
// Deliberate fix: legacy's select lacked the underscore form, so a
// POST_ONLY market rendered the raw wire value — map both directions instead.
export const MARKET_STATE_OPTIONS = ['ACTIVE', 'POST ONLY', 'SUSPENDED', 'CLOSED'] as const;
export type MarketStateLabel = (typeof MARKET_STATE_OPTIONS)[number];

export const toWireState = (label: MarketStateLabel): MarketState => (label === 'POST ONLY' ? 'POST_ONLY' : label);
export const toDisplayState = (state: MarketState): MarketStateLabel => (state === 'POST_ONLY' ? 'POST ONLY' : state);

// UNAUTHENTICATED, non-bop-api: the endpoint is public; the legacy Nuxt
// route fetched it with no authorization header.
export const getInstruments = () => fastApi.get(INSTRUMENTS_PATH).json<Market[]>();

// Deliberate fix: legacy sent the entire market object; bop-api's
// Zod schema strips everything but { id, state } — send only the slim payload.
export interface UpdateMarketRequest {
  id: string;
  /** machine value — 'POST_ONLY', never 'POST ONLY' */
  state: MarketState;
  /** audit justification — checked server-side before schema validation */
  comment: string;
}

// 200 responses are `{}` — .text() tolerates an empty body too.
export const updateMarket = (body: UpdateMarketRequest) => bopApi.post(UPDATE_MARKET_PATH, { json: body }).text();

export interface UpdateMarketsBulkRequest {
  /** min 1; server dedupes */
  ids: string[];
  state: MarketState;
  comment: string;
}

/** Partial success is HTTP 200 — callers must inspect `failed`. */
export interface UpdateMarketsBulkResponse {
  /** ids the exchange accepted */
  updated: string[];
  failed: Array<{ id: string; error: string }>;
}

export const updateMarketsBulk = (body: UpdateMarketsBulkRequest) => bopApi.post(UPDATE_MARKETS_BULK_PATH, { json: body }).json<UpdateMarketsBulkResponse>();

/**
 * GET shape: the *Percentage fields are HUMAN percents (10 = 10%); the
 * *PercentageInt fields are the exchange's scaled ints (1000 = 10%) — cross-check
 * only, the UI reads and sends the human percent.
 */
export interface PerpOrderLimit {
  pairCode: number;
  /** e.g. 'BTC_USD_P' */
  symbol: string;
  bidGoodPriceLimit: string;
  bidBadPriceLimit: string;
  askGoodPriceLimit: string;
  askBadPriceLimit: string;
  bidGoodPercentageInt: number;
  bidGoodPercentage: number;
  bidBadPercentageInt: number;
  bidBadPercentage: number;
  askGoodPercentageInt: number;
  askGoodPercentage: number;
  askBadPercentageInt: number;
  askBadPercentage: number;
  /** integer string */
  maxOrderValue: string;
}

/**
 * 404 means the exchange lists no order limits for the market, not an error —
 * mapped to null so the dialog opens with those fields blank.
 */
export const getPerpOrderLimits = async (symbol: string): Promise<PerpOrderLimit | null> => {
  try {
    return await bopApi.get(`${PERP_ORDER_LIMITS_PATH}/${symbol}`).json<PerpOrderLimit>();
  } catch (error) {
    if (isNotFoundError(error)) return null;
    throw error;
  }
};

/** Every futures market's limits in one call — feeds the read-only table columns. */
export const listPerpOrderLimits = () => bopApi.get(PERP_ORDER_LIMITS_PATH).json<PerpOrderLimit[]>();

/** Keyed by symbol so table rows can look up their limits in O(1). */
export const indexPerpOrderLimitsBySymbol = (limits: readonly PerpOrderLimit[]): ReadonlyMap<string, PerpOrderLimit> => new Map(limits.map((limit) => [limit.symbol, limit]));

/**
 * The single price-collar percentage BOP exposes. All four collars are written
 * with the same value, so reading any one of them is enough — first defined wins.
 * HUMAN percent (10 = 10%).
 */
export const toPriceCollarPercentage = (limits: PerpOrderLimit | null | undefined): number =>
  limits ? (limits.bidGoodPercentage ?? limits.bidBadPercentage ?? limits.askGoodPercentage ?? limits.askBadPercentage ?? 0) : 0;

/** Lenient numeric parse for the GET's value string. */
export const parseOrderValue = (value: string | number | undefined | null): number => {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number') return value;
  if (value.trim() === '') return 0;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

// A cap the exchange never initialised: 0 for markets loaded at boot, Long.MAX_VALUE for
// markets listed at runtime (reported with a decimal point inserted, hence the digit match).
const LONG_MAX_VALUE_DIGITS = '9223372036854775807';

/** The exchange reports 0 or the Long.MAX_VALUE sentinel for a limit that has never been set — both mean unlimited. */
export const isUnsetOrderLimit = (value: string | number | undefined | null): boolean =>
  parseOrderValue(value) === 0 || String(value).replace('.', '').replace(/^0+/, '') === LONG_MAX_VALUE_DIGITS;

/**
 * Collar the exchange applies to every perp pair on load (scaled int 9900 in
 * ExchangePairsLoader) until an operator sets one. Wide enough to be effectively open.
 */
export const DEFAULT_PRICE_COLLAR_PERCENTAGE = 99;

export const isDefaultPriceCollar = (limits: PerpOrderLimit | null | undefined): boolean => toPriceCollarPercentage(limits) === DEFAULT_PRICE_COLLAR_PERCENTAGE;

/** One cap whose published (public API) value differs from what the exchange enforces. */
export interface OrderLimitMismatch {
  /** null = published as "no cap"; undefined = not published at all (environment not re-seeded yet) */
  published: string | null | undefined;
  exchange: string;
}

export interface OrderLimitDrift {
  /** percent, e.g. "10" */
  priceCollar?: OrderLimitMismatch;
  maxOrderValue?: OrderLimitMismatch;
}

const isSameAmount = (published: string | null | undefined, exchange: string): boolean => {
  if (published === undefined) return false;
  if (published === null) return isUnsetOrderLimit(exchange);
  const a = toBigNumber(published);
  const b = toBigNumber(exchange);
  return a !== null && b !== null && a.eq(b);
};

const isSamePercentage = (published: string | null | undefined, exchange: number): boolean => {
  return toBigNumber(published)?.eq(exchange) ?? false;
};

/**
 * Compares the price collar and the order value cap the public API publishes for a futures
 * market against the exchange's live limits. Numeric comparison, so "55250" and "55250.00000"
 * agree, and a published null cap agrees with either unset sentinel on the exchange. Returns
 * null when there is nothing to compare (no exchange limits loaded, or a spot market) or when
 * both match; otherwise only the mismatching limits are present.
 */
export const getOrderLimitDrift = (market: Market, limits: PerpOrderLimit | undefined): OrderLimitDrift | null => {
  if (!limits || !isFuturesMarket(market)) return null;
  const exchangeCollar = toPriceCollarPercentage(limits);
  const drift: OrderLimitDrift = {
    ...(isSamePercentage(market.price_collar_percentage, exchangeCollar) ? {} : { priceCollar: { published: market.price_collar_percentage, exchange: String(exchangeCollar) } }),
    ...(isSameAmount(market.max_order_value, limits.maxOrderValue) ? {} : { maxOrderValue: { published: market.max_order_value, exchange: limits.maxOrderValue } }),
  };
  return drift.priceCollar || drift.maxOrderValue ? drift : null;
};

/** One segment's cap and the value the exchange enforces for it. */
export interface SegmentPositionLimit {
  /** the cap set for the segment; null when unset */
  cap: string | null;
  capInt: number | null;
  /** `cap` when set, else the market's baseMaxPos */
  effective: string;
  effectiveInt: number;
}

/** One row of GET v1/markets/positionLimits — the exchange's position ladder for a perp. */
export interface PositionLimit {
  /** e.g. 'BTC_USD_P' */
  symbol: string;
  pairCode: number;
  quote: string;
  /** the market default, quote notional; fixed when the market is listed */
  baseMaxPos: string;
  baseMaxPosInt: number;
  segments: Record<PositionLimitSegment, SegmentPositionLimit>;
}

/** Empty when the exchange build predates position limits — the table then renders without them. */
export const listPositionLimits = () => bopApi.get(POSITION_LIMITS_PATH).json<PositionLimit[]>();

/** Keyed by symbol so table rows can look up their limits in O(1). */
export const indexPositionLimitsBySymbol = (limits: readonly PositionLimit[]): ReadonlyMap<string, PositionLimit> => new Map(limits.map((limit) => [limit.symbol, limit]));

/** Thousands-grouped display of a limit; a non-numeric value is shown as it came. */
export const formatLimitAmount = (value: string): string => toBigNumber(value)?.toFormat() ?? value;

/** Per segment, a published max position size that differs from what the exchange enforces. */
export type PositionLimitDrift = Partial<Record<PositionLimitSegment, OrderLimitMismatch>>;

const isSamePositionSize = (published: string | undefined, exchange: string): boolean => {
  if (published === undefined) return false;
  const a = toBigNumber(published);
  const b = toBigNumber(exchange);
  return a !== null && b !== null && a.eq(b);
};

/**
 * Compares the max position sizes the public API publishes for a futures market against what
 * the exchange enforces per segment. Returns null when there is nothing to compare (the exchange
 * reports no position limits for the market, or a spot market) or when every segment matches;
 * otherwise only the mismatching segments are present.
 */
export const getPositionLimitDrift = (market: Market, positionLimit: PositionLimit | undefined): PositionLimitDrift | null => {
  if (!positionLimit || !isFuturesMarket(market)) return null;
  const published = market.max_position_size;
  const drift: PositionLimitDrift = {};
  for (const segment of POSITION_LIMIT_SEGMENTS) {
    const exchange = positionLimit.segments[segment].effective;
    if (published && isSamePositionSize(published[segment], exchange)) continue;
    // null = published as "no sizes"; undefined = not published at all (environment not re-seeded yet)
    drift[segment] = { published: published === null ? null : published?.[segment], exchange };
  }
  return Object.keys(drift).length > 0 ? drift : null;
};

/** Ids of the markets whose published limits disagree with the exchange, for the page-level warning. */
export const findDriftedMarketIds = (
  markets: readonly Market[],
  limitsBySymbol: ReadonlyMap<string, PerpOrderLimit>,
  positionLimitsBySymbol: ReadonlyMap<string, PositionLimit>,
): string[] =>
  markets
    .filter((market) => getOrderLimitDrift(market, limitsBySymbol.get(market.id)) !== null || getPositionLimitDrift(market, positionLimitsBySymbol.get(market.id)) !== null)
    .map((market) => market.id);

const REQUIRED_MESSAGE = 'This field is required';
const NUMBER_MESSAGE = 'Must be a valid number';
const WHOLE_NUMBER_MESSAGE = 'Must be a whole number';

const WHOLE_NUMBER_PATTERN = /^\d+$/;
// The exchange holds the collar as a scaled int, so no finer than 0.01%
const PRICE_COLLAR_PATTERN = /^\d*\.?\d{0,2}$/;

// Sanity bounds bop-api enforces on the order limits: human percent, and quote notional
export const MAX_PRICE_COLLAR_PERCENTAGE = 1000;
export const MAX_ORDER_VALUE = 100_000_000;

/** Validates an edited price collar field (human percent). Returns the message to show, or null when valid. */
export const validatePriceCollarPercentage = (value: string): string | null => {
  const trimmed = value.trim();
  if (trimmed === '') return REQUIRED_MESSAGE;
  const percentage = Number(trimmed);
  if (Number.isNaN(percentage)) return NUMBER_MESSAGE;
  if (percentage < 0) return 'Must not be negative';
  if (percentage > MAX_PRICE_COLLAR_PERCENTAGE) return `Must be at most ${formatLimitAmount(String(MAX_PRICE_COLLAR_PERCENTAGE))}`;
  if (!PRICE_COLLAR_PATTERN.test(trimmed)) return 'Must have at most 2 decimal places';
  return null;
};

/**
 * Validates an edited max order value field. Blank is the one thing an edit cannot produce: the
 * exchange rejects 0, so a value cap cannot be returned to unlimited once set.
 */
export const validateMaxOrderValue = (value: string): string | null => {
  const trimmed = value.trim();
  if (trimmed === '') return 'A value cap cannot be removed once set';
  if (Number.isNaN(Number(trimmed))) return NUMBER_MESSAGE;
  if (!WHOLE_NUMBER_PATTERN.test(trimmed)) return WHOLE_NUMBER_MESSAGE;
  if (Number(trimmed) <= 0) return 'Must be greater than 0';
  if (Number(trimmed) > MAX_ORDER_VALUE) return `Must be at most ${formatLimitAmount(String(MAX_ORDER_VALUE))}`;
  return null;
};

// Sanity bounds bop-api enforces on a segment cap, quote notional
export const MIN_SEGMENT_POSITION_CAP = 100;
export const MAX_SEGMENT_POSITION_CAP = 100_000_000;

/**
 * Validates one segment's cap field. Blank is valid — it means "not set", where the exchange
 * falls back to the market default. Returns the message to show, or null when valid.
 */
export const validateSegmentPositionCap = (value: string): string | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (Number.isNaN(Number(trimmed))) return NUMBER_MESSAGE;
  if (!WHOLE_NUMBER_PATTERN.test(trimmed)) return WHOLE_NUMBER_MESSAGE;
  const cap = Number(trimmed);
  if (cap < MIN_SEGMENT_POSITION_CAP || cap > MAX_SEGMENT_POSITION_CAP) {
    return `Must be between ${formatLimitAmount(String(MIN_SEGMENT_POSITION_CAP))} and ${formatLimitAmount(String(MAX_SEGMENT_POSITION_CAP))}`;
  }
  return null;
};

/**
 * One text field per limit of a futures market. A blank field means the exchange holds no value
 * for it: no price collar reported, no order value cap, or a segment on the market default.
 */
export interface MarketLimitsFormValues extends Record<PositionLimitSegment, string> {
  /** HUMAN percent (10 = 10%) */
  priceCollarPercentage: string;
  maxOrderValue: string;
}

export type MarketLimitsField = keyof MarketLimitsFormValues;

/**
 * What the exchange enforces → form values. `orderLimits` is null when the exchange lists no order
 * limits for the market (404) and `positionLimit` undefined when it reports no position ladder —
 * their fields then start blank.
 */
export const toMarketLimitsFormValues = (orderLimits: PerpOrderLimit | null, positionLimit: PositionLimit | undefined): MarketLimitsFormValues => ({
  priceCollarPercentage: orderLimits ? String(toPriceCollarPercentage(orderLimits)) : '',
  // toFixed: plain digits, so "55250.00000" opens as "55250"
  maxOrderValue: !orderLimits || isUnsetOrderLimit(orderLimits.maxOrderValue) ? '' : new BigNumber(orderLimits.maxOrderValue).toFixed(),
  RETAIL: positionLimit?.segments.RETAIL.cap ?? '',
  INSTITUTIONAL: positionLimit?.segments.INSTITUTIONAL.cap ?? '',
  MARKET_MAKER: positionLimit?.segments.MARKET_MAKER.cap ?? '',
});

/** True when the operator has not edited the field the dialog opened with. */
export const isLimitUnchanged = (value: string, initial: string): boolean => value.trim() === initial.trim();

/** Body of POST v1/markets/updateLimits. Every limit is optional: one left out keeps its value on the exchange. */
export interface UpdateMarketLimitsRequest {
  symbol: string;
  /** audit justification */
  comment: string;
  /** HUMAN percent (10 = 10%), the unit the GET reports. bop-api writes it to all four collars. */
  priceCollarPercentage?: number;
  /** whole-number STRING */
  maxOrderValue?: string;
  /** Per segment: a whole-number string, or null to clear the cap back to the market default. */
  caps: Partial<Record<PositionLimitSegment, string | null>>;
}

// Drops leading zeros, so "0250000" is sent as "250000". The bulk dialog counts edits from unvalidated input, so a
// value that isn't a number yet passes through rather than throwing
const toWholeNumber = (value: string): string => toBigNumber(value.trim())?.toFixed() ?? value.trim();
const toCap = (value: string): string | null => (value.trim() === '' ? null : toWholeNumber(value));

/**
 * Form → request. Only the limits the operator edited are sent, so a value the exchange holds in
 * a form this dialog would not accept (a decimal cap, a blank collar) is never rewritten by a save
 * that did not touch it. A blanked segment becomes null, which bop-api sends to the exchange as
 * "clear". A save with nothing edited only resyncs the public record.
 */
export const buildMarketLimitsRequest = (symbol: string, form: MarketLimitsFormValues & { comment: string }, initial: MarketLimitsFormValues): UpdateMarketLimitsRequest => {
  const isEdited = (field: MarketLimitsField) => !isLimitUnchanged(form[field], initial[field]);
  return {
    symbol,
    comment: form.comment,
    ...(isEdited('priceCollarPercentage') ? { priceCollarPercentage: Number(form.priceCollarPercentage.trim()) } : {}),
    ...(isEdited('maxOrderValue') ? { maxOrderValue: toWholeNumber(form.maxOrderValue) } : {}),
    caps: Object.fromEntries(POSITION_LIMIT_SEGMENTS.filter(isEdited).map((segment) => [segment, toCap(form[segment])])),
  };
};

export const hasLimitEdits = (request: UpdateMarketLimitsRequest): boolean =>
  request.priceCollarPercentage !== undefined || request.maxOrderValue !== undefined || Object.keys(request.caps).length > 0;

/** One request per market the operator edited, all under the same comment. Untouched markets are not sent. */
export const buildBulkMarketLimitsRequests = (
  rows: Readonly<Record<string, MarketLimitsFormValues>>,
  initialRows: Readonly<Record<string, MarketLimitsFormValues>>,
  comment: string,
): UpdateMarketLimitsRequest[] =>
  Object.entries(initialRows)
    .filter(([symbol]) => rows[symbol] !== undefined)
    .map(([symbol, initial]) => buildMarketLimitsRequest(symbol, { ...rows[symbol], comment }, initial))
    .filter(hasLimitEdits);

/**
 * One save for every limit of a futures market. bop-api writes the limits sent, re-reads the
 * exchange, mirrors what it enforces into the public instruments record and writes one audit log.
 * 200 responses are `{}`.
 */
export const updateMarketLimits = (body: UpdateMarketLimitsRequest) => bopApi.post(UPDATE_MARKET_LIMITS_PATH, { json: body }).text();

// Exchange danger-zone actions. All three take { comment } — the key is
// `comment`, NOT `justification` (the legacy Nuxt BFF did the rename).
export const freezeExchange = (comment: string) => bopApi.post(FREEZE_EXCHANGE_PATH, { json: { comment } }).text();

export const unfreezeExchange = (comment: string) => bopApi.post(UNFREEZE_EXCHANGE_PATH, { json: { comment } }).text();

/**
 * Kill additionally enforces a server-side email allowlist
 * (config.tradeEngine.staff.exchangeAdmins) — a 403 here means "not on the
 * allowlist"; membership is server-side only, never pre-checked client-side.
 */
export const killExchange = (comment: string) => bopApi.post(KILL_EXCHANGE_PATH, { json: { comment } }).text();

/** Typed verification phrase for the killswitch flow (stage 2). */
export const KILL_VERIFICATION_PHRASE = 'kill the exchange';
